import { useEffect, useRef } from "react";
import { Play, Headphones, Facebook, Youtube, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import FacebookLive from "@/components/FacebookLive";

const MediaHub = () => {
  const podcastRef = useRef(null);

  useEffect(() => {
    // Load Buzzsprout podcast player
    if (podcastRef.current && !podcastRef.current.querySelector('script')) {
      const script = document.createElement('script');
      script.src = 'https://www.buzzsprout.com/2518670.js?container_id=buzzsprout-large-player&player=large';
      script.type = 'text/javascript';
      script.charset = 'utf-8';
      script.async = true;
      podcastRef.current.appendChild(script);
    }
  }, []);

  return (
    <section id="media" className="section-spacing bg-zinc-50" data-testid="media-hub-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-12">
          <p className="text-overline mb-4" data-testid="media-overline">Stay Connected</p>
          <h2 className="heading-massive text-4xl sm:text-5xl text-black" data-testid="media-heading">
            Media Hub
          </h2>
        </div>

        {/* Bento Grid */}
        <div className="bento-grid">
          {/* Main YouTube Block */}
          <div className="bento-main card-hard-shadow bg-white overflow-hidden" data-testid="youtube-main-block">
            <div className="relative aspect-video bg-black">
              <iframe
                src="https://www.youtube.com/embed/5meTLBPUsc0"
                title="Latest Message - Light City Church"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
                data-testid="youtube-embed"
              />
            </div>
            <div className="p-6">
              <p className="text-overline text-zinc-500 mb-2">Latest Message</p>
              <h3 className="font-['Clash_Display'] font-semibold text-xl mb-3">
                Watch Our Latest Sermon
              </h3>
              <p className="font-['Satoshi'] text-zinc-600 mb-4">
                Join us every Sunday as we dive deep into God's Word and discover how to apply it to our daily lives.
              </p>
              <a
                href="https://www.youtube.com/@leonardlucas2293"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-['Satoshi'] font-semibold text-black hover:text-zinc-600 transition-colors"
                data-testid="youtube-watch-all-link"
              >
                Watch All Messages
                <ArrowUpRight size={18} />
              </a>
            </div>
          </div>

          {/* YouTube Subscribe */}
          <div className="card-hard-shadow bg-red-600 p-6 flex flex-col justify-between min-h-[200px]" data-testid="youtube-subscribe-block">
            <Youtube size={40} className="text-white" />
            <div>
              <h3 className="font-['Clash_Display'] font-semibold text-xl text-white mb-2">
                YouTube
              </h3>
              <p className="font-['Satoshi'] text-white/80 mb-4 text-sm">
                Subscribe for weekly sermons and worship content.
              </p>
              <a href="https://www.youtube.com/@leonardlucas2293" target="_blank" rel="noopener noreferrer">
                <Button
                  className="btn-sharp bg-white text-red-600 hover:bg-red-100 px-6 py-2 text-sm w-full"
                  data-testid="youtube-subscribe-btn"
                >
                  Subscribe
                </Button>
              </a>
            </div>
          </div>

          {/* Facebook */}
          <div className="card-hard-shadow bg-blue-600 p-6 flex flex-col justify-between min-h-[200px]" data-testid="facebook-block">
            <Facebook size={40} className="text-white" />
            <div>
              <h3 className="font-['Clash_Display'] font-semibold text-xl text-white mb-2">
                Facebook
              </h3>
              <p className="font-['Satoshi'] text-white/80 mb-4 text-sm">
                Join our community for updates and live streams.
              </p>
              <a href="https://www.facebook.com/LightCityNola" target="_blank" rel="noopener noreferrer">
                <Button
                  className="btn-sharp bg-white text-blue-600 hover:bg-blue-100 px-6 py-2 text-sm w-full"
                  data-testid="facebook-follow-btn"
                >
                  Follow Us
                </Button>
              </a>
            </div>
          </div>

          {/* Podcast */}
          <div className="card-hard-shadow bg-black p-6 flex flex-col justify-between min-h-[200px]" data-testid="podcast-block">
            <Headphones size={40} className="text-[#FFB800]" />
            <div>
              <h3 className="font-['Clash_Display'] font-semibold text-xl text-white mb-2">
                Podcast
              </h3>
              <p className="font-['Satoshi'] text-white/60 mb-4 text-sm">
                Listen to messages on the go with our podcast.
              </p>
              <a href="https://www.buzzsprout.com/2518670" target="_blank" rel="noopener noreferrer">
                <Button
                  className="btn-sharp bg-[#FFB800] text-black hover:bg-[#e5a600] px-6 py-2 text-sm w-full"
                  data-testid="podcast-listen-btn"
                >
                  Listen Now
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Live Stream Banner */}
        <div className="mt-8 card-hard-shadow bg-white p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-testid="livestream-banner">
          <div className="flex items-center gap-4">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            <div>
              <h3 className="font-['Clash_Display'] font-semibold text-xl">
                Watch Live Every Sunday
              </h3>
              <p className="font-['Satoshi'] text-zinc-600">
                Join us at 11:00 AM for our live worship experience
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href="https://www.facebook.com/share/v/1ES8qtY88A/" target="_blank" rel="noopener noreferrer">
              <Button
                className="btn-sharp bg-blue-600 text-white hover:bg-blue-700 px-8 py-3 text-sm flex items-center gap-2"
                data-testid="watch-live-fb-btn"
              >
                <Facebook size={18} />
                Watch on Facebook
              </Button>
            </a>
            <a href="https://www.youtube.com/@leonardlucas2293" target="_blank" rel="noopener noreferrer">
              <Button
                className="btn-sharp bg-black text-white hover:bg-white hover:text-black hover:shadow-[inset_0_0_0_2px_black] px-8 py-3 text-sm flex items-center gap-2"
                data-testid="watch-live-btn"
              >
                <Youtube size={18} />
                Watch on YouTube
              </Button>
            </a>
          </div>
        </div>

        {/* Podcast Player */}
        <div className="mt-8 card-hard-shadow bg-white p-8" data-testid="podcast-player-section">
          <div className="flex items-center gap-3 mb-6">
            <Headphones size={28} className="text-[#FFB800]" />
            <h3 className="font-['Clash_Display'] font-semibold text-xl">
              Light City Podcast
            </h3>
          </div>
          <div id="buzzsprout-large-player" ref={podcastRef} data-testid="buzzsprout-player" />
        </div>

        {/* Facebook Live Video */}
        <FacebookLive />
      </div>
    </section>
  );
};

export default MediaHub;
